import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { RHFTextField, useFormValidator } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { ArrowUpIcon } from '@tyro/icons';
import { AnimatePresence, m } from 'motion/react';
import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTyronePrompt } from '../../../../api/tyrone/prompt';
import type { TyroneMessageThread } from './types';

interface FormState {
  prompt: string;
}

interface ConversationViewProps {
  messageThread: TyroneMessageThread;
  setMessageThread: React.Dispatch<React.SetStateAction<TyroneMessageThread>>;
  onTitleChange?: (title: string) => void;
  isHistoryPending?: boolean;
  refetchHistory?: () => void;
}

export function ConversationView({
  messageThread,
  setMessageThread,
  onTitleChange,
  isHistoryPending,
  refetchHistory,
}: ConversationViewProps) {
  const { t } = useTranslation(['common']);
  const { customShadows } = useTheme();
  const messageContainerRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: sendPrompt, isPending } = useTyronePrompt();

  useEffect(() => {
    if (messageThread.conversationId && !isHistoryPending) {
      refetchHistory?.();
    }
  }, []);

  const { resolver, rules } = useFormValidator<FormState>();
  const { control, handleSubmit, watch, reset } = useForm<FormState>({
    resolver: resolver({
      prompt: rules.required(),
    }),
  });

  const onSubmit = handleSubmit(({ prompt }) => {
    const isNewConversation =
      !messageThread.conversationId && messageThread.messages.length === 0;
    if (isNewConversation) {
      onTitleChange?.(prompt);
    }
    setMessageThread((prev) => ({
      ...prev,
      messages: [
        ...(prev?.messages || []),
        {
          id: nanoid(8),
          by: 'user',
          text: prompt,
        },
      ],
    }));
    reset();
    sendPrompt(
      {
        conversationId: messageThread?.conversationId,
        prompt,
      },
      {
        onSuccess: ({ tyrone_prompt }) => {
          setMessageThread((prev) => ({
            ...prev,
            conversationId: tyrone_prompt.conversationId,
            messages: [
              ...(prev?.messages || []),
              {
                id: nanoid(8),
                by: 'ai',
                text: tyrone_prompt.result,
              },
            ],
          }));
        },
      },
    );
  });

  useEffect(() => {
    if (messageContainerRef.current) {
      const lastMessage = messageContainerRef.current.lastElementChild;
      if (lastMessage) {
        lastMessage.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messageThread.messages, isPending]);

  const prompt = watch('prompt');

  return (
    <>
      <Box
        ref={messageContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,

          '& .message': {
            maxWidth: '80%',
            p: 1.5,
            borderRadius: 2,
            boxShadow: customShadows.card,

            '&.user': {
              alignSelf: 'flex-end',
              backgroundColor: 'indigo.100',
            },

            '&.ai': {
              alignSelf: 'flex-start',
              backgroundColor: 'background.paper',
            },
          },
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {messageThread.messages.map((msg, index) => (
            <Box
              key={msg.id}
              component={m.div}
              layoutId={`message-${index}`}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`message ${msg.by}`}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {msg.text}
              </Typography>
            </Box>
          ))}
          {isPending && (
            <Box
              key="loading"
              component={m.div}
              layoutId={`message-${messageThread.messages.length}`}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              sx={{
                alignSelf: 'flex-start',
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: customShadows.card,
                width: 'fit-content',
              }}
            >
              <Stack direction="row" spacing={0.5}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    component={m.div}
                    animate={{ y: [0, -6, 0] }}
                    transition={{
                      duration: 0.6,
                      repeat: Number.POSITIVE_INFINITY,
                      delay: i * 0.1,
                      ease: 'easeInOut',
                    }}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </AnimatePresence>
      </Box>

      <Stack p={1} width="100%">
        <RHFTextField
          controlProps={{
            control,
            name: 'prompt',
          }}
          variant="white-filled-outlined"
          textFieldProps={{
            placeholder: 'Ask anything...',
            multiline: true,
            minRows: 2,
            maxRows: 4,
            fullWidth: true,
            slotProps: {
              input: {
                'aria-label': 'prompt',
                hiddenLabel: true,
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit();
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Fab
                      size="small"
                      aria-label={t('common:actions.send')}
                      onClick={onSubmit}
                      disabled={isPending || !(prompt?.length > 0)}
                    >
                      <ArrowUpIcon />
                    </Fab>
                  </InputAdornment>
                ),
              },
            },
          }}
        />
      </Stack>
    </>
  );
}
